import { Container, Graphics, Sprite, Text, type Renderer } from 'pixi.js';

import {
  project,
  venueId,
  type County,
  type CountyVenue,
  type OfferedJob,
  type Projection,
} from '@hotlist/shared';

import { Icons, type IconName } from './icons.ts';
import type { Viewport } from './viewport.ts';

/**
 * Where the job you took actually goes.
 *
 * The work board named two towns and the map showed nothing, so a job was a
 * line of text and a destination node. Now the pickup and the drop are objects
 * on the ground with the venue's own icon and its own name -- a pharmacy looks
 * like a pharmacy, a scrap yard looks like a scrap yard.
 *
 * That is not decoration. §8's whole argument about routine is that a life
 * becomes legible through the *specific* places somebody keeps returning to,
 * and a map that renders every errand as an anonymous dot cannot show anybody
 * that. The most quietly alarming thing in one of these datasets is that the
 * system knows which Tuesday is the pharmacy, and this is the pharmacy.
 *
 * Only the job in hand is drawn. Every offer on the board at once would be a
 * map covered in markers, which is the overhead-view superpower §4 rules out.
 */
export class MarkerLayer {
  readonly container = new Container();

  private readonly icons: Icons;
  private readonly projection: Projection;
  private readonly sprites = new Map<string, { icon: Sprite; label: Text }>();
  private current: OfferedJob | null = null;
  private stage: 'collect' | 'deliver' = 'collect';
  /** Every filling station in the county, and its sprite while it is on screen. */
  private readonly fuel: readonly CountyVenue[];
  private readonly pumps = new Map<string, { disc: Sprite; glyph: Sprite }>();
  /** A ring that pulses at a world point and fades. Where "look here" points. */
  private flash: { x: number; y: number; until: number; ring: Graphics } | null = null;

  constructor(renderer: Renderer, projection: Projection, county: County) {
    this.projection = projection;
    this.icons = new Icons(renderer);
    // Filling stations are not a thing you discover. Everybody knows where the
    // petrol is, and a player who runs dry looking for one has been beaten by
    // the interface rather than by the game.
    this.fuel = county.venues.filter((venue) => venue.kind === 'fuel');
  }

  /** The job in hand and which leg of it, or nothing. */
  setJob(job: OfferedJob | null, stage: 'collect' | 'deliver' = 'collect'): void {
    this.current = job;
    this.stage = stage;
    if (job === null) {
      for (const { icon, label } of this.sprites.values()) {
        icon.destroy();
        label.destroy();
      }
      this.sprites.clear();
    }
  }

  /**
   * Pulse a ring at a world position for a few seconds.
   *
   * The answer to "the alert says Lannon Road heading west and I cannot find
   * it": clicking the alert pans the map, and this is the mark that says
   * *here*, because a pan alone leaves the eye searching the same screen it
   * was already searching.
   */
  lookAt(x: number, y: number): void {
    if (this.flash) {
      this.flash.ring.destroy();
      this.flash = null;
    }
    const ring = new Graphics();
    this.container.addChild(ring);
    this.flash = { x, y, until: performance.now() + 4000, ring };
  }

  update(viewport: Viewport): void {
    if (this.flash) {
      const now = performance.now();
      if (now >= this.flash.until) {
        this.flash.ring.destroy();
        this.flash = null;
      } else {
        const p = project(this.projection, this.flash.x, this.flash.y);
        const phase = ((now / 900) % 1) * 26 + 8;
        const fade = Math.min(1, (this.flash.until - now) / 1500);
        this.flash.ring.clear();
        this.flash.ring
          .circle(viewport.toScreenX(p.sx), viewport.toScreenY(p.sy), phase)
          .stroke({ width: 3, color: 0xff5a45, alpha: 0.9 * fade });
        this.flash.ring.zIndex = 50;
      }
    }
    this.paintFuel(viewport);

    const job = this.current;
    if (!job) return;

    const visible = viewport.visibleBounds(160);
    // Big enough to hit with a thumb, small enough not to cover the junction.
    const size = Math.max(20, Math.min(46, 26 * viewport.scale * 30));

    // Both ends are drawn, and the one being driven to is lit. A job is two
    // drives and a map that only marked the destination could not show that.
    const going = this.stage === 'collect' ? 'from' : 'to';
    // The leg not being driven is drawn subdued but must still be findable: at
    // 0x55616f it was a dark slate that disappeared into the dark base map, so
    // "where does this job actually go" had no answer until you collected. A
    // light slate keeps it clearly secondary to the lit leg yet always visible.
    const dim = 0x9aa8bc;
    const ends: [string, { name: string; kind: string; x: number; y: number }, number][] = [
      ['from', job.fromVenue, going === 'from' ? 0xffb020 : dim],
      ['to', job.toVenue, going === 'to' ? (job.illegal ? 0xd98f4a : 0x4bd6a0) : dim],
    ];

    for (const [key, venue, color] of ends) {
      const p = project(this.projection, venue.x, venue.y);
      const onScreen =
        p.sx >= visible.minX && p.sx <= visible.maxX && p.sy >= visible.minY && p.sy <= visible.maxY;

      let held = this.sprites.get(key);
      if (!onScreen) {
        if (held) {
          held.icon.visible = false;
          held.label.visible = false;
        }
        continue;
      }

      if (!held) {
        // The venue's own icon when it has one, and the plain pin otherwise --
        // a venue kind arriving off the wire is a string, and a string this
        // client has never heard of must not take the map down.
        const name: IconName = this.icons.has(venue.kind)
          ? venue.kind
          : key === 'to'
            ? 'drop'
            : 'pickup';
        const icon = new Sprite(this.icons.get(name));
        icon.anchor.set(0.5, 1);
        const label = new Text({
          text: venue.name,
          style: {
            fontFamily: 'ui-monospace, monospace',
            fontSize: 11,
            fill: color,
          },
        });
        label.anchor.set(0.5, 0);
        this.container.addChild(icon, label);
        held = { icon, label };
        this.sprites.set(key, held);
      }

      const x = viewport.toScreenX(p.sx);
      const y = viewport.toScreenY(p.sy);
      held.icon.visible = true;
      held.label.visible = true;
      held.icon.position.set(x, y);
      held.icon.width = size;
      held.icon.height = size;
      held.icon.tint = color;
      held.label.position.set(x, y + 3);
    }
  }

  /**
   * The filling stations, which are simply on the map.
   *
   * Not discovered, not reported, not earned: §8's discovery rule is about
   * *cameras*, because a camera is the thing the other side would rather you
   * could not find. A petrol station has a lit sign on a pole and a forecourt,
   * and a player driving around a real county unable to find one has been
   * beaten by the interface rather than by the game. The fuel gauge already
   * names the nearest one; this is where it is.
   *
   * Below the zoom where they would be a scatter of dots they drop out, the
   * same rule the road classes follow -- seventy-eight pumps at county scale is
   * a texture, not information.
   */
  private paintFuel(viewport: Viewport): void {
    const visible = viewport.visibleBounds(80);
    // Smaller than the job markers and much smaller than a camera. A filling
    // station is somewhere to go when the tank is low, not a thing competing
    // for attention with the objects the game is about.
    const size = 9 * viewport.scale * 30;
    const live = new Set<string>();

    if (size >= 9) {
      for (const venue of this.fuel) {
        const p = project(this.projection, venue.x, venue.y);
        if (
          p.sx < visible.minX ||
          p.sx > visible.maxX ||
          p.sy < visible.minY ||
          p.sy > visible.maxY
        ) {
          continue;
        }
        const key = venueId(venue.name, venue.x, venue.y);
        live.add(key);

        let held = this.pumps.get(key);
        if (!held) {
          const disc = new Sprite(this.icons.get('disc'));
          disc.anchor.set(0.5, 0.5);
          disc.tint = 0x080b10;
          disc.alpha = 0.7;
          const glyph = new Sprite(this.icons.get('fuel'));
          glyph.anchor.set(0.5, 0.5);
          // Warm and pale rather than the blue-grey it was, which on a dark map
          // was hard to pick out and on aerial imagery was not there at all.
          glyph.tint = 0xe4c98a;
          // Underneath the job markers: where you are going matters more than
          // where you could buy petrol.
          this.container.addChildAt(glyph, 0);
          this.container.addChildAt(disc, 0);
          held = { disc, glyph };
          this.pumps.set(key, held);
        }
        const x = viewport.toScreenX(p.sx);
        const y = viewport.toScreenY(p.sy);
        const wide = Math.min(20, size);
        held.disc.position.set(x, y);
        held.disc.width = wide * 1.12;
        held.disc.height = wide * 1.12;
        held.glyph.position.set(x, y);
        held.glyph.width = wide;
        held.glyph.height = wide;
      }
    }

    for (const [id, held] of this.pumps) {
      if (live.has(id)) continue;
      held.disc.destroy();
      held.glyph.destroy();
      this.pumps.delete(id);
    }
  }
}
